import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { returnHomeIfRequested } from "../lib/setupRoute";
import {
    ICON_OPTIONS,
    PRODUCT_CATEGORIES,
    SERVICE_CATEGORIES,
} from "../data/seed";

/**
 * Catalog — Módulo de Catálogo & Estoque.
 * Navegação em cards de tela cheia (hub → lista → formulário), mesmo padrão
 * do Painel Financeiro/WhatsApp — sem modal pra criar/editar item, então o
 * formulário rola normalmente com a página (sem scroll-trap no mobile).
 *  - Toggle Ativo/Inativo (itens inativos somem do Terminal Balcão)
 *  - Alerta visual quando estoque <= estoque mínimo (produtos)
 */
const Catalog = () => {
    const {
        services,
        products,
        upsertService,
        deleteService,
        toggleService,
        upsertProduct,
        deleteProduct,
        toggleProduct,
        setActiveModule,
    } = useSalon();

    const [view, setView] = useState("hub"); // "hub" | "list" | "form"
    const [tab, setTab] = useState("services"); // qual catálogo a lista/form atual está mostrando
    const [query, setQuery] = useState("");
    const [showInactive, setShowInactive] = useState(true);
    const [editing, setEditing] = useState(null); // item sendo criado/editado no form

    const isProducts = tab === "products";
    const items = isProducts ? products : services;
    const categories = isProducts ? PRODUCT_CATEGORIES : SERVICE_CATEGORIES;

    const openList = (nextTab) => {
        setTab(nextTab);
        setQuery("");
        setView("list");
    };

    const openCreateForm = () => {
        setEditing(
            isProducts
                ? { name: "", price: 0, category: PRODUCT_CATEGORIES[0], stock: 0, minStock: 0, icon: "fa-jar", active: true, commission: "" }
                : { name: "", price: 0, category: SERVICE_CATEGORIES[0], duration: 30, icon: "fa-scissors", active: true, commission: "" }
        );
        setView("form");
    };

    const openEditForm = (item) => {
        setEditing(item);
        setView("form");
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items.filter((it) => {
            const matchesQuery =
                !q ||
                it.name.toLowerCase().includes(q) ||
                (it.category || "").toLowerCase().includes(q);
            const matchesActive = showInactive || it.active !== false;
            return matchesQuery && matchesActive;
        });
    }, [items, query, showInactive]);

    const stats = useMemo(() => {
        const total = items.length;
        const active = items.filter((i) => i.active !== false).length;
        const lowStock = isProducts
            ? products.filter((p) => p.active !== false && (p.stock ?? 0) <= (p.minStock ?? 0)).length
            : 0;
        const totalValue = isProducts
            ? products.reduce((s, p) => s + (p.price * (p.stock ?? 0)), 0)
            : 0;
        return { total, active, inactive: total - active, lowStock, totalValue };
    }, [items, isProducts, products]);

    const productsLowStock = products.filter((p) => p.active !== false && (p.stock ?? 0) <= (p.minStock ?? 0)).length;

    const handleSave = async (draft) => {
        if (!draft.name?.trim()) {
            toast.error("Informe o nome do item.");
            return;
        }
        const price = parseFloat(draft.price);
        if (Number.isNaN(price) || price < 0) {
            toast.error("Preço inválido.");
            return;
        }
        // Comissão: opcional. Se vazia/null = usa padrão do colaborador.
        let commission = null;
        if (draft.commission !== "" && draft.commission !== null && draft.commission !== undefined) {
            const c = parseFloat(draft.commission);
            if (Number.isNaN(c) || c < 0 || c > 100) {
                toast.error("Comissão deve estar entre 0 e 100%.");
                return;
            }
            commission = c;
        }
        const payload = {
            ...draft,
            price,
            commission,
            active: draft.active !== false,
        };
        if (isProducts) {
            payload.stock = Math.max(0, parseInt(draft.stock || 0, 10));
            payload.minStock = Math.max(0, parseInt(draft.minStock || 0, 10));
            await Promise.resolve(upsertProduct(payload));
        } else {
            payload.duration = Math.max(0, parseInt(draft.duration || 0, 10));
            await upsertService(payload);
        }
        toast.success(draft.id ? "Item atualizado" : "Item criado", {
            description: `${payload.name} salvo com sucesso.`,
        });
        setEditing(null);
        // Veio do checklist "Comece com o pé direito"? Volta pra Home; senão, lista.
        if (!returnHomeIfRequested(() => setActiveModule("home"))) setView("list");
    };

    const handleDelete = (item) => {
        if (!window.confirm(`Excluir "${item.name}"?`)) return;
        if (isProducts) deleteProduct(item.id);
        else deleteService(item.id);
        toast.success("Item removido");
    };

    const handleToggle = (item) => {
        if (isProducts) toggleProduct(item.id);
        else toggleService(item.id);
        const willActivate = item.active === false;
        toast.success(willActivate ? "Item reativado" : "Item desativado", {
            description: willActivate
                ? `${item.name} voltou a aparecer no Terminal Balcão.`
                : `${item.name} ocultado do Terminal Balcão.`,
        });
    };

    // ─── HUB ────────────────────────────────────────────────────────────────
    if (view === "hub") {
        return (
            <div data-testid="catalog-hub" className="space-y-6">
                <header>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Módulo</div>
                    <h2 className="salon-heading text-2xl text-white mt-1">Catálogo & Estoque</h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Gerencie os serviços oferecidos e os produtos do seu estoque.
                    </p>
                </header>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 auto-rows-fr max-w-2xl">
                    <NavCard
                        testId="catalog-card-services"
                        icon="fa-scissors"
                        title="Serviços"
                        description={`${services.filter((s) => s.active !== false).length} ativos de ${services.length}`}
                        accent="from-amber-500/30 to-amber-400/5"
                        onClick={() => openList("services")}
                    />
                    <NavCard
                        testId="catalog-card-products"
                        icon="fa-box-archive"
                        title="Produtos"
                        description={productsLowStock > 0 ? `${productsLowStock} com estoque baixo` : `${products.length} cadastrados`}
                        badge={productsLowStock > 0 ? "red" : null}
                        accent={productsLowStock > 0 ? "from-red-500/25 to-amber-500/5" : "from-emerald-500/25 to-amber-500/5"}
                        onClick={() => openList("products")}
                    />
                </div>
            </div>
        );
    }

    // ─── FORM (criar/editar) ────────────────────────────────────────────────
    if (view === "form" && editing) {
        return (
            <ItemFormView
                item={editing}
                isProducts={isProducts}
                categories={categories}
                onSave={handleSave}
                onBack={() => { setEditing(null); setView("list"); }}
            />
        );
    }

    // ─── LIST (serviços OU produtos) ────────────────────────────────────────
    return (
        <div data-testid="catalog-module" className="space-y-5">
            <BackButton onClick={() => setView("hub")} testId="catalog-list-back" />

            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Catálogo & Estoque</div>
                    <h2 className="salon-heading text-2xl text-white mt-1">{isProducts ? "Produtos" : "Serviços"}</h2>
                </div>
                <button
                    data-testid="catalog-new-btn"
                    onClick={openCreateForm}
                    className="min-h-[48px] px-5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold uppercase tracking-[0.15em] text-xs transition-all shadow-lg shadow-amber-500/20 flex items-center gap-2"
                >
                    <i className="fa-solid fa-plus"></i>
                    Novo {isProducts ? "Produto" : "Serviço"}
                </button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label={`${isProducts ? "Produtos" : "Serviços"} ativos`} value={`${stats.active}/${stats.total}`} icon="fa-circle-check" color="#10b981" />
                <Kpi label="Desativados" value={stats.inactive} icon="fa-eye-slash" color="#94a3b8" />
                {isProducts ? (
                    <>
                        <Kpi label="Estoque baixo" value={stats.lowStock} icon="fa-triangle-exclamation" color={stats.lowStock > 0 ? "#ef4444" : "#94a3b8"} />
                        <Kpi label="Valor em estoque" value={`R$ ${stats.totalValue.toFixed(0)}`} icon="fa-warehouse" color="#d97706" />
                    </>
                ) : (
                    <>
                        <Kpi label="Categorias" value={SERVICE_CATEGORIES.length} icon="fa-tags" color="#6366f1" />
                        <Kpi label="Preço médio" value={`R$ ${(services.reduce((s, x) => s + x.price, 0) / Math.max(1, services.length)).toFixed(0)}`} icon="fa-chart-simple" color="#d97706" />
                    </>
                )}
            </div>

            {/* Filters */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[240px] flex items-center gap-2 rounded-xl border border-white/10 bg-slate-950/60 px-3 focus-within:border-amber-500/60 transition-colors">
                    <i className="fa-solid fa-magnifying-glass text-slate-400 text-sm"></i>
                    <input
                        data-testid="catalog-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={`Buscar ${isProducts ? "produtos" : "serviços"} por nome ou categoria…`}
                        className="flex-1 bg-transparent py-2.5 text-sm text-white outline-none min-h-[40px] placeholder:text-slate-500"
                    />
                </div>
                <label className="flex items-center gap-3 cursor-pointer select-none min-h-[44px]">
                    <span className="text-[11px] uppercase tracking-wider text-slate-400">
                        Mostrar inativos
                    </span>
                    <Toggle
                        checked={showInactive}
                        onChange={() => setShowInactive((s) => !s)}
                        testId="catalog-show-inactive"
                    />
                </label>
            </div>

            {/* Table */}
            <div
                data-testid="catalog-table"
                className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl overflow-hidden"
            >
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.25em] text-slate-500 bg-slate-950/60">
                                <th className="text-left py-3 px-4">Item</th>
                                <th className="text-left py-3 px-4">Categoria</th>
                                <th className="text-right py-3 px-4">Preço</th>
                                {isProducts ? (
                                    <>
                                        <th className="text-center py-3 px-4">Estoque</th>
                                        <th className="text-center py-3 px-4">Mínimo</th>
                                    </>
                                ) : (
                                    <th className="text-center py-3 px-4">Duração</th>
                                )}
                                <th className="text-center py-3 px-4">Comissão</th>
                                <th className="text-center py-3 px-4">Ativo</th>
                                <th className="text-right py-3 px-4">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={isProducts ? 8 : 7} className="py-14 text-center text-slate-500">
                                        <i className="fa-solid fa-box-open text-3xl mb-3 opacity-40"></i>
                                        <p>Nenhum item encontrado</p>
                                    </td>
                                </tr>
                            )}
                            {filtered.map((it) => {
                                const lowStock = isProducts && (it.stock ?? 0) <= (it.minStock ?? 0);
                                const inactive = it.active === false;
                                return (
                                    <tr
                                        key={it.id}
                                        data-testid={`catalog-row-${it.id}`}
                                        onClick={() => openEditForm(it)}
                                        className={`border-t border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${
                                            inactive ? "opacity-50" : ""
                                        }`}
                                    >
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                                                    style={{ background: "rgba(217,119,6,0.15)", color: "#fbbf24" }}
                                                >
                                                    <i className={`fa-solid ${it.icon || "fa-tag"}`}></i>
                                                </div>
                                                <div>
                                                    <div className="text-white font-semibold">{it.name}</div>
                                                    {inactive && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                                                            <i className="fa-solid fa-eye-slash"></i>
                                                            Oculto no PDV
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-white/5 text-slate-300 text-[10px] uppercase tracking-wider font-semibold border border-white/5">
                                                {it.category || "—"}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right font-mono text-white">
                                            R$ {Number(it.price).toFixed(2)}
                                        </td>
                                        {isProducts ? (
                                            <>
                                                <td className="py-3 px-4 text-center">
                                                    <span
                                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-mono text-sm font-bold ${
                                                            lowStock
                                                                ? "bg-red-500/15 text-red-300 border border-red-500/30"
                                                                : "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
                                                        }`}
                                                    >
                                                        {lowStock && <i className="fa-solid fa-triangle-exclamation text-[10px]"></i>}
                                                        {it.stock ?? 0}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-center font-mono text-slate-400">
                                                    {it.minStock ?? 0}
                                                </td>
                                            </>
                                        ) : (
                                            <td className="py-3 px-4 text-center text-slate-300 font-mono">
                                                {it.duration ?? 0} min
                                            </td>
                                        )}
                                        <td className="py-3 px-4 text-center text-slate-400 font-mono text-xs">
                                            {it.commission != null && it.commission !== "" ? `${it.commission}%` : "padrão"}
                                        </td>
                                        <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex justify-center">
                                                <Toggle
                                                    checked={it.active !== false}
                                                    onChange={() => handleToggle(it)}
                                                    testId={`catalog-toggle-${it.id}`}
                                                />
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="inline-flex items-center gap-1">
                                                <button
                                                    data-testid={`catalog-edit-${it.id}`}
                                                    onClick={() => openEditForm(it)}
                                                    title="Editar"
                                                    className="h-9 w-9 rounded-lg bg-white/5 hover:bg-amber-500/20 hover:text-amber-300 text-slate-400 transition-all"
                                                >
                                                    <i className="fa-solid fa-pen text-xs"></i>
                                                </button>
                                                <button
                                                    data-testid={`catalog-delete-${it.id}`}
                                                    onClick={() => handleDelete(it)}
                                                    title="Excluir"
                                                    className="h-9 w-9 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 text-slate-400 transition-all"
                                                >
                                                    <i className="fa-solid fa-trash text-xs"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-3 border-t border-white/5 text-[11px] text-slate-500 flex items-center justify-between flex-wrap gap-2">
                    <span>
                        Exibindo <span className="text-white font-mono">{filtered.length}</span> de{" "}
                        <span className="text-white font-mono">{items.length}</span>
                    </span>
                    <span>
                        <i className="fa-solid fa-circle-info mr-1"></i>
                        Itens desativados não aparecem no Terminal Balcão
                    </span>
                </div>
            </div>
        </div>
    );
};

// ─── Nav Card (hub) — mesmo padrão visual da Central de Clientes ─────────────
const NavCard = ({ testId, icon, title, description, badge, accent = "from-amber-500/30 to-amber-400/5", onClick }) => (
    <div
        onClick={onClick}
        data-testid={testId}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
            }
        }}
        className="group relative rounded-2xl border border-amber-500/20 bg-black/50 p-5 text-left overflow-hidden transition-all duration-300 cursor-pointer hover:border-amber-400/55 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(212,175,55,0.45)] active:translate-y-0 active:scale-[0.99]"
    >
        {/* Background gradient */}
        <div className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-60 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}></div>

        <div className="relative h-full flex flex-col min-h-[170px]">
            {/* Icon */}
            <div className="h-14 w-14 rounded-2xl flex items-center justify-center border border-amber-400/30 bg-gradient-to-br from-amber-500/25 to-amber-700/5 text-amber-300 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
                <i className={`fa-solid ${icon} text-xl`}></i>
            </div>

            {/* Title + subtitle */}
            <div className="mt-auto">
                <h3 className="salon-heading text-lg lg:text-xl text-amber-100 leading-tight">{title}</h3>
                <p className="text-[11px] text-amber-200/55 mt-1 leading-snug">{description}</p>
                {badge && (
                    <span className={`mt-2 inline-block text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                        badge === "red"
                            ? "bg-red-500/20 text-red-300 border border-red-500/30"
                            : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                    }`}>
                        {badge === "red" ? "Atenção" : "Pendente"}
                    </span>
                )}
            </div>

            {/* Arrow */}
            <div className="absolute bottom-0 right-0 text-amber-300/40 group-hover:text-amber-200 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all">
                <i className="fa-solid fa-arrow-up-right"></i>
            </div>
        </div>

        {/* Bottom hover accent */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
    </div>
);

const BackButton = ({ onClick, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className="h-10 w-10 rounded-xl border border-white/10 hover:border-amber-500/40 text-slate-400 hover:text-amber-300 flex items-center justify-center transition-all"
    >
        <i className="fa-solid fa-arrow-left"></i>
    </button>
);

// ─── Form (criar/editar) — página cheia, sem modal ─────────────────────────
const ItemFormView = ({ item, isProducts, categories, onSave, onBack }) => {
    const [draft, setDraft] = useState(item);
    const [saving, setSaving] = useState(false);
    const isNew = !item.id;

    const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const handleSubmit = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await onSave(draft);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="catalog-form-view" className="space-y-5">
            <BackButton onClick={onBack} testId="catalog-form-back" />

            <header>
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400">
                    {isProducts ? "Produto" : "Serviço"}
                </div>
                <h2 className="salon-heading text-2xl text-white mt-1">
                    {isNew ? `Novo ${isProducts ? "produto" : "serviço"}` : draft.name}
                </h2>
            </header>

            <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5 lg:p-6 space-y-4 max-w-2xl">
                <Field label="Nome" testId="catalog-input-name">
                    <input
                        value={draft.name || ""}
                        onChange={(e) => setField("name", e.target.value)}
                        placeholder="Ex: Corte clássico"
                        className={inputCls}
                        data-testid="catalog-input-name-input"
                    />
                </Field>

                <div className="grid grid-cols-2 gap-4">
                    <Field label="Preço (R$)" testId="catalog-input-price">
                        <input
                            type="number"
                            step="0.01"
                            value={draft.price ?? 0}
                            onChange={(e) => setField("price", e.target.value)}
                            className={`${inputCls} font-mono`}
                            data-testid="catalog-input-price-input"
                        />
                    </Field>
                    <Field label="Categoria" testId="catalog-input-category">
                        <select
                            value={draft.category || categories[0]}
                            onChange={(e) => setField("category", e.target.value)}
                            className={inputCls}
                            data-testid="catalog-input-category-input"
                        >
                            {categories.map((c) => (
                                <option key={c} value={c} className="bg-slate-950">
                                    {c}
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>

                {isProducts ? (
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Quantidade atual" testId="catalog-input-stock">
                            <input
                                type="number"
                                min={0}
                                value={draft.stock ?? 0}
                                onChange={(e) => setField("stock", e.target.value)}
                                className={`${inputCls} font-mono`}
                                data-testid="catalog-input-stock-input"
                            />
                        </Field>
                        <Field label="Estoque mínimo (alerta)" testId="catalog-input-min">
                            <input
                                type="number"
                                min={0}
                                value={draft.minStock ?? 0}
                                onChange={(e) => setField("minStock", e.target.value)}
                                className={`${inputCls} font-mono`}
                                data-testid="catalog-input-min-input"
                            />
                        </Field>
                    </div>
                ) : (
                    <Field label="Duração (minutos)" testId="catalog-input-duration">
                        <input
                            type="number"
                            min={0}
                            value={draft.duration ?? 0}
                            onChange={(e) => setField("duration", e.target.value)}
                            className={`${inputCls} font-mono`}
                            data-testid="catalog-input-duration-input"
                        />
                    </Field>
                )}

                {/* Comissão por item — opcional, sobrepõe a comissão do colaborador */}
                <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-3">
                    <div className="flex items-center gap-2">
                        <i className="fa-solid fa-percent text-amber-400"></i>
                        <span className="text-[11px] uppercase tracking-[0.25em] text-amber-300 font-semibold">
                            Comissão deste item
                        </span>
                    </div>
                    <Field label="Porcentagem (%) — opcional" testId="catalog-input-commission">
                        <input
                            type="number"
                            min={0}
                            max={100}
                            step="0.5"
                            value={draft.commission ?? ""}
                            onChange={(e) => setField("commission", e.target.value)}
                            placeholder="Deixe em branco para usar a comissão padrão do colaborador"
                            className={`${inputCls} font-mono`}
                            data-testid="catalog-input-commission-input"
                        />
                    </Field>
                    <p className="text-[11px] text-amber-200/70 leading-relaxed">
                        <i className="fa-solid fa-circle-info mr-1"></i>
                        Se preenchido, este valor <strong>sobrepõe</strong> a comissão base do
                        colaborador no PDV. Se em branco, será usada a comissão padrão do
                        profissional que executou o atendimento.
                    </p>
                </div>

                <Field label="Ícone (FontAwesome)" testId="catalog-input-icon">
                    <div className="grid grid-cols-8 gap-1.5 max-h-32 overflow-y-auto pr-1">
                        {ICON_OPTIONS.map((ic) => {
                            const active = draft.icon === ic;
                            return (
                                <button
                                    key={ic}
                                    type="button"
                                    data-testid={`catalog-icon-${ic}`}
                                    onClick={() => setField("icon", ic)}
                                    className={`aspect-square min-h-[44px] rounded-lg flex items-center justify-center transition-all border ${
                                        active
                                            ? "border-amber-500/60 bg-amber-500/10 text-amber-300"
                                            : "border-white/5 bg-slate-950/60 text-slate-400 hover:text-white"
                                    }`}
                                >
                                    <i className={`fa-solid ${ic}`}></i>
                                </button>
                            );
                        })}
                    </div>
                </Field>

                <label className="flex items-center gap-3 cursor-pointer select-none">
                    <Toggle
                        checked={draft.active !== false}
                        onChange={() => setField("active", !(draft.active !== false))}
                        testId="catalog-input-active"
                    />
                    <span className="text-sm text-slate-300">
                        {draft.active !== false
                            ? "Item ativo — visível no Terminal Balcão"
                            : "Item inativo — oculto do Terminal Balcão"}
                    </span>
                </label>
            </div>

            <div className="flex items-center justify-end gap-3 max-w-2xl">
                <button
                    data-testid="catalog-form-cancel"
                    onClick={onBack}
                    className="min-h-[48px] px-5 rounded-xl border border-white/10 hover:border-white/30 text-slate-300 hover:text-white text-xs uppercase tracking-wider transition-all"
                >
                    Cancelar
                </button>
                <button
                    data-testid="catalog-form-save"
                    onClick={handleSubmit}
                    disabled={saving}
                    className="min-h-[48px] px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold uppercase tracking-[0.15em] text-xs transition-all shadow-lg shadow-amber-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-floppy-disk"} mr-2`}></i>
                    {saving ? "Salvando…" : isNew ? "Criar item" : "Salvar alterações"}
                </button>
            </div>
        </div>
    );
};

// ─── Helpers ───────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange, testId }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-testid={testId}
        onClick={onChange}
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors min-h-[28px] ${
            checked ? "bg-amber-500" : "bg-slate-700"
        }`}
    >
        <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                checked ? "translate-x-6" : "translate-x-1"
            }`}
        />
    </button>
);

const Kpi = ({ label, value, icon, color = "#d97706" }) => (
    <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-4">
        <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
            <div
                className="h-8 w-8 rounded-lg flex items-center justify-center text-sm"
                style={{ background: `${color}22`, color }}
            >
                <i className={`fa-solid ${icon}`}></i>
            </div>
        </div>
        <div className="salon-heading text-2xl text-white mt-2">{value}</div>
    </div>
);

const Field = ({ label, testId, children }) => (
    <label className="block" data-testid={testId}>
        <span className="text-[11px] uppercase tracking-[0.25em] text-slate-400">{label}</span>
        <div className="mt-1.5">{children}</div>
    </label>
);

const inputCls =
    "w-full bg-slate-950/60 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-amber-500/60 transition-colors min-h-[48px]";

export default Catalog;
